// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  COUNTRIES,
  Country,
  getCountryByCode,
  getCountryLabel,
} from '@/constants/country';
import { PlaceOutlined } from '@/constants/hardfin-standard-icons';
import { useGooglePlaces } from '@/contexts/google-places-context';
import {
  AddressComponents,
  extractAddressComponents,
  findComponent,
  formatAddressFromComponents,
} from '@/location/utilities/extract-address-components';
import { LocationComponents } from '@/sparse/api/types';
import {
  Autocomplete,
  Box,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import { omit } from 'lodash-es';
import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface AddressValue {
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  address: LocationComponents;
}

const EMPTY_COMPONENTS: AddressComponents = {
  addressLine1: null,
  addressLine2: null,
  locality: null,
  administrativeArea: null,
  postalCode: null,
  countryCode: null,
};

/** Fields that affect geocoding results */
const GEO_SIGNIFICANT_FIELDS: ReadonlySet<keyof AddressComponents> = new Set([
  'addressLine1',
  'locality',
  'administrativeArea',
  'postalCode',
  'countryCode',
]);

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The current address value.
   */
  value: AddressValue | null;

  /**
   * A callback fired when the address changes.
   */
  onChange: (value: AddressValue | null) => void;
}

interface Prediction {
  placeId: string;
  description: string;
}

/**
 * Checks whether all address component fields are empty.
 */
function isAddressEmpty(components: AddressComponents): boolean {
  return (
    !components.addressLine1 &&
    !components.addressLine2 &&
    !components.locality &&
    !components.administrativeArea &&
    !components.postalCode &&
    !components.countryCode
  );
}

/**
 * Derives display-ready address components from the stored value. When the
 * address only has a formattedAddress with no structured component fields,
 * seeds addressLine1 with the formatted string so the form shows the existing
 * address text instead of blank fields. The user can then refine it via
 * autocomplete or manual editing.
 */
function getDisplayComponents(value: AddressValue | null): AddressComponents {
  const address = value?.address;
  if (!address) {
    return EMPTY_COMPONENTS;
  }

  const components: AddressComponents = {
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    locality: address.locality,
    administrativeArea: address.administrativeArea,
    postalCode: address.postalCode,
    countryCode: address.countryCode,
  };

  // When only formattedAddress is present, seed addressLine1 so the form
  // shows the address text instead of blank fields
  if (isAddressEmpty(components) && address.formattedAddress) {
    return { ...components, addressLine1: address.formattedAddress };
  }

  return components;
}

/**
 * Renders structured address fields with Google Places autocomplete on Address
 * line 1. All 6 fields are always visible.
 */
function AddressSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { isReady, getPredictions, getDetails, geocodeAddress } =
    useGooglePlaces();
  const valueRef = useRef(props.value);
  valueRef.current = props.value;
  const hasAttemptedGeocode = useRef(false);

  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debouncedGetPredictions = useMemo(
    () =>
      debounce(async (input: string) => {
        const results = await getPredictions(input);
        setPredictions(
          results.map((r) => ({
            placeId: r.place_id,
            description: r.description,
          }))
        );
        setIsSearching(false);
      }, 500),
    [getPredictions]
  );

  // Cancel any pending debounced call on unmount to avoid stale state updates
  useEffect(() => {
    return () => {
      debouncedGetPredictions.clear();
    };
  }, [debouncedGetPredictions]);

  // Auto-geocode legacy addresses that have only a formattedAddress with no
  // structured components. This populates the component fields so the user
  // sees a fully decomposed address when editing.
  const currentValue = props.value;
  const onChange = props.onChange;
  useEffect(() => {
    if (hasAttemptedGeocode.current || !isReady) {
      return;
    }

    const address = currentValue?.address;
    if (!address?.formattedAddress) {
      return;
    }

    const currentComponents: AddressComponents = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      locality: address.locality,
      administrativeArea: address.administrativeArea,
      postalCode: address.postalCode,
      countryCode: address.countryCode,
    };

    if (!isAddressEmpty(currentComponents)) {
      return;
    }

    hasAttemptedGeocode.current = true;

    geocodeAddress(address.formattedAddress).then((result) => {
      if (!result?.address_components) {
        return;
      }

      const resolved = extractAddressComponents(result.address_components);
      const latitude = result.geometry?.location?.lat() ?? null;
      const longitude = result.geometry?.location?.lng() ?? null;
      const placeId = result.place_id ?? null;

      onChange({
        placeId,
        latitude,
        longitude,
        address: {
          ...resolved,
          formattedAddress: formatAddressFromComponents(resolved),
        },
      });
    });
  }, [isReady, currentValue, geocodeAddress, onChange]);

  const components = getDisplayComponents(props.value);

  const handleInputChange = (_: unknown, input: string, reason: string) => {
    // Only update on user keystrokes, not when MUI resets the input after
    // an option selection
    if (reason !== 'input') {
      return;
    }

    const current = props.value?.address ?? EMPTY_COMPONENTS;
    const updated = { ...current, addressLine1: input || null };

    if (isAddressEmpty(updated)) {
      props.onChange(null);
    } else {
      // Address line 1 is geo-significant, so clear placeId/lat/lon
      props.onChange({
        placeId: null,
        latitude: null,
        longitude: null,
        address: {
          ...updated,
          formattedAddress: formatAddressFromComponents(updated),
        },
      });
    }

    if (input.length > 0) {
      setIsSearching(true);
      debouncedGetPredictions(input);
    } else {
      setPredictions([]);
    }
  };

  // Place Autocomplete drops a suite or unit the user typed, and can offer a
  // wrong place for it, such as reading "Suite 2872" as street number 2872.
  // Geocoding the typed text inside the selected place's postal code returns
  // the unit as its own component. We use that result only when it is a unit on
  // the same street as the selection, and otherwise keep the selection.
  const geocodeUnit = async (
    typedAddress: string,
    selectedComponents: google.maps.GeocoderAddressComponent[]
  ): Promise<google.maps.GeocoderResult | null> => {
    const postalCode = findComponent(selectedComponents, 'postal_code');
    const country = findComponent(selectedComponents, 'country');
    const route = findComponent(selectedComponents, 'route');

    if (typedAddress === '' || !postalCode || !country || !route) {
      return null;
    }

    // Geocoding rejects when nothing matches; any failure keeps the selection
    const unit = await geocodeAddress(typedAddress, {
      postalCode: postalCode.long_name,
      country: country.short_name,
    }).catch(() => null);

    if (
      !unit ||
      !findComponent(unit.address_components, 'subpremise') ||
      findComponent(unit.address_components, 'route')?.long_name !==
        route.long_name
    ) {
      return null;
    }

    return unit;
  };

  const handleSelectPrediction = async (
    _: unknown,
    selected: string | Prediction | null
  ) => {
    if (selected === null || typeof selected === 'string') {
      return;
    }

    // Read the typed text before awaiting, while it still holds what the user
    // entered rather than the selected option
    const typedAddress = valueRef.current?.address.addressLine1 ?? '';

    const result = await getDetails(selected.placeId);
    const latitude = result?.geometry?.location?.lat();
    const longitude = result?.geometry?.location?.lng();

    if (
      latitude === undefined ||
      longitude === undefined ||
      result?.formatted_address === undefined ||
      result?.place_id === undefined
    ) {
      return;
    }

    const unit = await geocodeUnit(
      typedAddress,
      result.address_components ?? []
    );
    const components = unit?.address_components ?? result.address_components;
    const resolved = components
      ? extractAddressComponents(components)
      : EMPTY_COMPONENTS;

    props.onChange({
      placeId: unit?.place_id ?? result.place_id,
      latitude: unit?.geometry.location.lat() ?? latitude,
      longitude: unit?.geometry.location.lng() ?? longitude,
      address: {
        ...resolved,
        formattedAddress: formatAddressFromComponents(resolved),
      },
    });
  };

  const handleChangeComponent =
    (field: keyof AddressComponents) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const current = props.value?.address ?? EMPTY_COMPONENTS;
      const updated = {
        ...current,
        [field]: event.target.value || null,
      };

      // If all fields are empty, set value to null
      if (isAddressEmpty(updated)) {
        return props.onChange(null);
      }

      // Geo invalidation: editing geo-significant fields clears placeId/lat/lon
      const isGeoSignificant = GEO_SIGNIFICANT_FIELDS.has(field);
      const latestValue = props.value;

      props.onChange({
        placeId: isGeoSignificant ? null : (latestValue?.placeId ?? null),
        latitude: isGeoSignificant ? null : (latestValue?.latitude ?? null),
        longitude: isGeoSignificant ? null : (latestValue?.longitude ?? null),
        address: {
          ...updated,
          formattedAddress: formatAddressFromComponents(updated),
        },
      });
    };

  const handleChangeCountry = (_: unknown, selected: Country | null) => {
    const current = props.value?.address ?? EMPTY_COMPONENTS;
    const updated = { ...current, countryCode: selected?.code ?? null };

    if (isAddressEmpty(updated)) {
      return props.onChange(null);
    }

    // Country is geo-significant, so clear placeId/lat/lon
    props.onChange({
      placeId: null,
      latitude: null,
      longitude: null,
      address: {
        ...updated,
        formattedAddress: formatAddressFromComponents(updated),
      },
    });
  };

  return (
    <Box>
      <Box
        component="fieldset"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          m: 0,
        }}
      >
        <Typography
          component="legend"
          variant="subtitle2"
          sx={{ px: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <PlaceOutlined fontSize="small" color="action" />
          Address
        </Typography>
        <Stack spacing={1.5}>
          <Autocomplete
            ref={ref}
            freeSolo
            size="small"
            options={predictions}
            loading={isSearching}
            disabled={!isReady}
            inputValue={components.addressLine1 ?? ''}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.description
            }
            filterOptions={(options) => options}
            onInputChange={handleInputChange}
            onChange={handleSelectPrediction}
            renderInput={(params) => (
              <TextField
                {...params}
                name={props.name}
                label="Street address"
                error={props.error}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'address-line1',
                }}
              />
            )}
          />
          <TextField
            size="small"
            label="Address line 2"
            autoComplete="address-line2"
            value={components.addressLine2 ?? ''}
            onChange={handleChangeComponent('addressLine2')}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              label="City/locality"
              autoComplete="address-level2"
              sx={{ flex: 2 }}
              value={components.locality ?? ''}
              onChange={handleChangeComponent('locality')}
            />
            <TextField
              size="small"
              label="State/province"
              autoComplete="address-level1"
              sx={{ flex: 1 }}
              value={components.administrativeArea ?? ''}
              onChange={handleChangeComponent('administrativeArea')}
            />
            <TextField
              size="small"
              label="Postal code"
              autoComplete="postal-code"
              sx={{ flex: 1 }}
              value={components.postalCode ?? ''}
              onChange={handleChangeComponent('postalCode')}
            />
          </Stack>
          <Autocomplete
            size="small"
            options={COUNTRIES}
            value={getCountryByCode(components.countryCode)}
            onChange={handleChangeCountry}
            getOptionLabel={getCountryLabel}
            groupBy={(option) => option.group}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'country',
                }}
              />
            )}
            renderOption={(optionProps, option) => (
              <li {...omit(optionProps, 'key')} key={option.code}>
                {option.flag} {option.name} ({option.code})
              </li>
            )}
          />
        </Stack>
      </Box>
      {props.helperText && (
        <FormHelperText error={props.error}>{props.helperText}</FormHelperText>
      )}
    </Box>
  );
}

/**
 * A controlled component for entering an address with structured fields.
 * Address line 1 is a freeSolo autocomplete with Google Places predictions.
 * Selecting a prediction populates all fields. Editing geo-significant fields
 * (all except addressLine2) clears placeId/lat/lon.
 */
export const AddressSearch = forwardRef(AddressSearchRenderer);
